import React, { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { apiGet, apiPatch, apiPost } from '../../api';
import { useAsync } from '../../hooks/useAsync';
import type { AccessLink, Activity, Company, DataRoomAccessMode, FolderNode, Person, Probability } from '../../types';
import { companyTypeLabel, fundraisingStageLabel, probabilityLabel } from '../../i18n';
import { useToast } from '../../components/ToastHost';

type Resp = {
  company: Company;
  people: Person[];
  links: AccessLink[];
  activities: Activity[];
  timeline?: Array<{
    id: string;
    kind: 'note' | 'meeting' | 'visit' | 'email';
    occurredAt: string;
    title: string;
    content: string;
    metadata?: any;
  }>;
  folderAccess: { allowedFolderIds: string[] };
};

const PROBS: Probability[] = ['Bajo', 'Medio', 'Alto'];

type DataRoomTreeResp = {
  folders: FolderNode[];
};

function buildChildren(folders: FolderNode[]) {
  const map = new Map<string | null, FolderNode[]>();
  folders.forEach((f) => {
    const k = f.parentId || null;
    map.set(k, [...(map.get(k) || []), f]);
  });
  map.forEach((arr) => arr.sort((a, b) => a.sortOrder - b.sortOrder || a.name.localeCompare(b.name)));
  return map;
}

export default function AdminCompanyDetail() {
  const { id } = useParams();
  const companyId = id || '';
  const { data, loading, error, setData } = useAsync<Resp>(() => apiGet(`/api/admin/companies/${companyId}`), [companyId]);
  const { data: treeData } = useAsync<DataRoomTreeResp>(() => apiGet('/api/admin/data-room/tree'), []);
  const toast = useToast();

  const [personName, setPersonName] = useState('');
  const [personEmail, setPersonEmail] = useState('');
  const [personRole, setPersonRole] = useState('');
  const [ticketExact, setTicketExact] = useState('');
  const [ticketMin, setTicketMin] = useState('');
  const [ticketMax, setTicketMax] = useState('');
  const [currency, setCurrency] = useState('USD');
  const [prob, setProb] = useState<Probability>('Medio');
  const [leadsRound, setLeadsRound] = useState(false);
  const [metricsDetailAccess, setMetricsDetailAccess] = useState(false);

  const [activityType, setActivityType] = useState<'note' | 'meeting'>('note');
  const [activityDate, setActivityDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [activityContent, setActivityContent] = useState('');

  const folders = treeData?.folders || [];
  const folderChildren = useMemo(() => buildChildren(folders), [folders]);

  const [allowedFolderIdsDraft, setAllowedFolderIdsDraft] = useState<string[]>([]);

  const [savingCompany, setSavingCompany] = useState(false);
  const [savingAccessMode, setSavingAccessMode] = useState<DataRoomAccessMode | null>(null);
  const [savingFolders, setSavingFolders] = useState(false);
  const [addingPerson, setAddingPerson] = useState(false);
  const [addingActivity, setAddingActivity] = useState(false);
  const [busyPerson, setBusyPerson] = useState<{ primaryId?: string | null; emailId?: string | null; linkId?: string | null }>({});

  const linksByPerson = useMemo(() => {
    const map = new Map<string, AccessLink[]>();
    (data?.links || []).forEach((l) => {
      map.set(l.personId, [...(map.get(l.personId) || []), l]);
    });
    map.forEach((arr) => arr.sort((a, b) => b.createdAt.localeCompare(a.createdAt)));
    return map;
  }, [data]);

  useEffect(() => {
    if (!data) return;
    setCurrency(data.company.currency || 'USD');
    setProb(data.company.probability || 'Medio');
    setTicketExact(data.company.ticketExact != null ? String(data.company.ticketExact) : '');
    setTicketMin(data.company.ticketMin != null ? String(data.company.ticketMin) : '');
    setTicketMax(data.company.ticketMax != null ? String(data.company.ticketMax) : '');
    setLeadsRound(Boolean(data.company.leadsRound));
    setMetricsDetailAccess(Boolean(data.company.metricsDetailAccess));
  }, [data?.company?.id]);

  useEffect(() => {
    const next = data?.folderAccess?.allowedFolderIds || [];
    setAllowedFolderIdsDraft(next);
  }, [data?.company?.id, data?.folderAccess?.allowedFolderIds?.join('|')]);

  const refresh = async () => {
    const next = await apiGet<Resp>(`/api/admin/companies/${companyId}`);
    setData(next);
  };

  const addPerson = async () => {
    setAddingPerson(true);
    try {
      await apiPost(`/api/admin/companies/${companyId}/people`, {
        name: personName,
        email: personEmail || null,
        role: personRole || null,
      });
      setPersonName('');
      setPersonEmail('');
      setPersonRole('');
      await refresh();
      toast.success('Person added.');
    } catch (e: any) {
      toast.error(e?.payload?.error || e?.message || 'Could not add person.');
      throw e;
    } finally {
      setAddingPerson(false);
    }
  };

  const genLink = async (personId: string) => {
    const existingUrl = (linksByPerson.get(personId) || [])[0]?.url;
    if (existingUrl) {
      try {
        await navigator.clipboard.writeText(existingUrl);
        toast.success('Link copied.');
      } catch {
        toast.info('Copy not supported in this browser.');
      }
      return;
    }

    setBusyPerson((p) => ({ ...p, linkId: personId }));
    try {
      const res = await apiPost<{ link: AccessLink }>(`/api/admin/people/${personId}/links`, {});
      await refresh();
      try {
        await navigator.clipboard.writeText(res.link.url);
        toast.success('Link copied.');
      } catch {
        toast.success('Link created.');
      }
    } catch (e: any) {
      toast.error(e?.payload?.error || e?.message || 'Could not create link.');
      throw e;
    } finally {
      setBusyPerson((p) => ({ ...p, linkId: null }));
    }
  };

  const sendEmail = async (personId: string) => {
    setBusyPerson((p) => ({ ...p, emailId: personId }));
    try {
      const res = await apiPost<{ ok: boolean; url?: string; error?: string }>(`/api/admin/people/${personId}/send-link-email`, {});
      if (!res.ok) {
        toast.error(res.error || 'Could not send email.');
        return;
      }
      toast.success('Email sent.');
    } catch (e: any) {
      toast.error(e?.payload?.error || e?.message || 'Could not send email.');
      throw e;
    } finally {
      setBusyPerson((p) => ({ ...p, emailId: null }));
    }
  };

  const setPrimary = async (personId: string) => {
    setBusyPerson((p) => ({ ...p, primaryId: personId }));
    try {
      await apiPost(`/api/admin/people/${personId}/primary`, {});
      await refresh();
      toast.success('Primary contact updated.');
    } catch (e: any) {
      toast.error(e?.payload?.error || e?.message || 'Could not update primary contact.');
      throw e;
    } finally {
      setBusyPerson((p) => ({ ...p, primaryId: null }));
    }
  };

  const saveCompany = async () => {
    setSavingCompany(true);
    try {
      await apiPatch(`/api/admin/companies/${companyId}`, {
        currency,
        probability: prob,
        ticketExact: ticketExact ? Number(ticketExact) : null,
        ticketMin: ticketMin ? Number(ticketMin) : null,
        ticketMax: ticketMax ? Number(ticketMax) : null,
        leadsRound,
        metricsDetailAccess,
      });
      await refresh();
      toast.success('Saved.');
    } catch (e: any) {
      toast.error(e?.payload?.error || e?.message || 'Could not save.');
      throw e;
    } finally {
      setSavingCompany(false);
    }
  };

  const saveDataRoomAccess = async (mode: DataRoomAccessMode) => {
    setSavingAccessMode(mode);
    try {
      await apiPatch(`/api/admin/companies/${companyId}/data-room`, { accessMode: mode, allowedFolderIds: data?.folderAccess?.allowedFolderIds || [] });
      await refresh();
      toast.success('Data Room access updated.');
    } catch (e: any) {
      toast.error(e?.payload?.error || e?.message || 'Could not update Data Room access.');
      throw e;
    } finally {
      setSavingAccessMode(null);
    }
  };

  const toggleAllowedFolder = (folderId: string) => {
    setAllowedFolderIdsDraft((prev) => {
      const set = new Set(prev);
      if (set.has(folderId)) set.delete(folderId);
      else set.add(folderId);
      return Array.from(set);
    });
  };

  const saveFolderAllowlist = async () => {
    setSavingFolders(true);
    try {
      await apiPatch(`/api/admin/companies/${companyId}/data-room`, { accessMode: 'folders', allowedFolderIds: allowedFolderIdsDraft });
      await refresh();
      toast.success('Folder access saved.');
    } catch (e: any) {
      toast.error(e?.payload?.error || e?.message || 'Could not save folder access.');
      throw e;
    } finally {
      setSavingFolders(false);
    }
  };

  const addActivity = async () => {
    setAddingActivity(true);
    try {
      await apiPost(`/api/admin/companies/${companyId}/activities`, {
        type: activityType,
        occurredAt: activityDate,
        content: activityContent,
      });
      setActivityContent('');
      await refresh();
      toast.success('Saved.');
    } catch (e: any) {
      toast.error(e?.payload?.error || e?.message || 'Could not save.');
      throw e;
    } finally {
      setAddingActivity(false);
    }
  };

  if (loading) return <div className="card">Loading…</div>;
  if (error || !data) return <div className="card">Error: {error || 'not found'}</div>;

  const c = data.company;

  return (
    <div className="grid" style={{ gap: 14 }}>
      <div className="card">
        <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
          <div>
            <div style={{ fontSize: 20, fontWeight: 950 }}>{c.name}</div>
            <div className="muted" style={{ fontSize: 12 }}>
              {companyTypeLabel(c.type)} · {c.country || '—'} · Stage: {fundraisingStageLabel(c.stage)}
            </div>
          </div>
          <a className="btn" href="/admin/companies">
            Back
          </a>
        </div>
      </div>

      <div className="card">
        <div style={{ fontSize: 16, fontWeight: 900, marginBottom: 10 }}>Ticket & probability</div>
        <div className="row">
          <div>
            <div className="muted" style={{ fontSize: 12, marginBottom: 6 }}>
              Currency
            </div>
            <input className="input" value={currency} onChange={(e) => setCurrency(e.target.value)} />
          </div>
          <div>
            <div className="muted" style={{ fontSize: 12, marginBottom: 6 }}>
              Probability
            </div>
            <select className="select" value={prob} onChange={(e) => setProb(e.target.value as any)}>
              {PROBS.map((p) => (
                <option key={p} value={p}>
                  {probabilityLabel(p)}
                </option>
              ))}
            </select>
          </div>
          <div>
            <div className="muted" style={{ fontSize: 12, marginBottom: 6 }}>
              Ronda
            </div>
            <label style={{ display: 'flex', gap: 10, alignItems: 'center', height: 40 }}>
              <input type="checkbox" checked={leadsRound} onChange={(e) => setLeadsRound(e.target.checked)} />
              <span>Lidera Ronda?</span>
            </label>
          </div>
          <div>
            <div className="muted" style={{ fontSize: 12, marginBottom: 6 }}>
              Metrics detail
            </div>
            <label style={{ display: 'flex', gap: 10, alignItems: 'center', height: 40 }}>
              <input type="checkbox" checked={metricsDetailAccess} onChange={(e) => setMetricsDetailAccess(e.target.checked)} />
              <span>Enable retention detail in investor portal</span>
            </label>
          </div>
        </div>
        <div style={{ height: 10 }} />
        <div className="row">
          <div>
            <div className="muted" style={{ fontSize: 12, marginBottom: 6 }}>
              Exact ticket
            </div>
            <input className="input" value={ticketExact} onChange={(e) => setTicketExact(e.target.value)} placeholder="Ex: 250000" />
          </div>
          <div>
            <div className="muted" style={{ fontSize: 12, marginBottom: 6 }}>
              Range (min)
            </div>
            <input className="input" value={ticketMin} onChange={(e) => setTicketMin(e.target.value)} placeholder="Ex: 100000" />
          </div>
          <div>
            <div className="muted" style={{ fontSize: 12, marginBottom: 6 }}>
              Range (max)
            </div>
            <input className="input" value={ticketMax} onChange={(e) => setTicketMax(e.target.value)} placeholder="Ex: 500000" />
          </div>
          <div style={{ display: 'flex', alignItems: 'flex-end' }}>
            <button className="btn primary" onClick={saveCompany} disabled={savingCompany}>
              {savingCompany ? 'Saving…' : 'Save'}
            </button>
          </div>
        </div>
      </div>

      <div className="card">
        <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
          <div>
            <div style={{ fontSize: 16, fontWeight: 900 }}>Data Room access</div>
            <div className="muted" style={{ fontSize: 12 }}>
              Current mode: <span className="pill">{c.dataRoomAccessMode}</span>
            </div>
          </div>
          <div className="segmented" role="radiogroup" aria-label="Data Room access mode">
            <button
              type="button"
              className={`segmented__btn ${c.dataRoomAccessMode === 'none' ? 'is-active' : ''}`}
              onClick={() => saveDataRoomAccess('none')}
              disabled={savingAccessMode != null}
              role="radio"
              aria-checked={c.dataRoomAccessMode === 'none'}
            >
              No access
            </button>
            <button
              type="button"
              className={`segmented__btn ${c.dataRoomAccessMode === 'all' ? 'is-active' : ''}`}
              onClick={() => saveDataRoomAccess('all')}
              disabled={savingAccessMode != null}
              role="radio"
              aria-checked={c.dataRoomAccessMode === 'all'}
            >
              Full access
            </button>
            <button
              type="button"
              className={`segmented__btn ${c.dataRoomAccessMode === 'folders' ? 'is-active' : ''}`}
              onClick={() => saveDataRoomAccess('folders')}
              disabled={savingAccessMode != null}
              role="radio"
              aria-checked={c.dataRoomAccessMode === 'folders'}
            >
              By folder
            </button>
          </div>
        </div>
        <div style={{ height: 10 }} />
        {c.dataRoomAccessMode !== 'folders' ? (
          <div className="muted" style={{ fontSize: 12 }}>
            Switch to <span className="pill">By folder</span> to select allowed folders.
          </div>
        ) : (
          <>
            <div className="muted" style={{ fontSize: 12, marginBottom: 10 }}>
              Select allowed folders. Subfolders inherit access.
            </div>
            {!folders.length ? (
              <div className="muted" style={{ fontSize: 12 }}>
                Create folders first in <a href="/admin/data-room">Data Room</a>.
              </div>
            ) : (
              <>
                <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap', alignItems: 'center' }}>
                  <div className="muted" style={{ fontSize: 12 }}>
                    Enabled folders: <span className="pill">{allowedFolderIdsDraft.length}</span>
                  </div>
                  <button className="btn primary" onClick={saveFolderAllowlist} disabled={savingFolders}>
                    {savingFolders ? 'Saving…' : 'Save'}
                  </button>
                </div>
                <div style={{ height: 10 }} />
                <div className="tree">
                  <ul>
                    {folders
                      .filter((f) => f.parentId == null)
                      .map((root) => (
                        <FolderAllowRow
                          key={root.id}
                          folder={root}
                          children={folderChildren}
                          allowedSet={new Set(allowedFolderIdsDraft)}
                          onToggle={toggleAllowedFolder}
                        />
                      ))}
                  </ul>
                </div>
              </>
            )}
          </>
        )}
      </div>

      <div className="card">
        <div style={{ fontSize: 16, fontWeight: 900, marginBottom: 10 }}>People & links</div>
        <div className="row">
          <div>
            <div className="muted" style={{ fontSize: 12, marginBottom: 6 }}>
              Name
            </div>
            <input className="input" value={personName} onChange={(e) => setPersonName(e.target.value)} placeholder="Name" />
          </div>
          <div>
            <div className="muted" style={{ fontSize: 12, marginBottom: 6 }}>
              Email
            </div>
            <input className="input" value={personEmail} onChange={(e) => setPersonEmail(e.target.value)} placeholder="email@..." />
          </div>
          <div>
            <div className="muted" style={{ fontSize: 12, marginBottom: 6 }}>
              Role
            </div>
            <input className="input" value={personRole} onChange={(e) => setPersonRole(e.target.value)} placeholder="Partner, Analyst, ..." />
          </div>
          <div style={{ display: 'flex', alignItems: 'flex-end' }}>
            <button className="btn primary" disabled={!personName.trim() || addingPerson} onClick={addPerson}>
              {addingPerson ? 'Adding…' : 'Add'}
            </button>
          </div>
        </div>
        <div style={{ height: 10 }} />
        <table className="table">
          <thead>
            <tr>
              <th>Person</th>
              <th>Contact</th>
              <th>Primary</th>
              <th>Links</th>
              <th style={{ textAlign: 'right' }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {data.people.map((p) => (
              <tr key={p.id}>
                <td>
                  <div style={{ fontWeight: 900 }}>{p.name}</div>
                  <div className="muted" style={{ fontSize: 12 }}>
                    {p.role || '—'}
                  </div>
                </td>
                <td className="muted">{p.email || '—'}</td>
                <td className="muted">{p.isPrimary ? 'Yes' : '—'}</td>
                <td className="muted">
                  {(linksByPerson.get(p.id) || []).length}
                  {(linksByPerson.get(p.id) || []).length ? (
                    <div className="muted" style={{ fontSize: 12, marginTop: 4 }}>
                      Last used:{' '}
                      {(linksByPerson.get(p.id) || [])[0]?.lastUsedAt
                        ? String((linksByPerson.get(p.id) || [])[0].lastUsedAt)
                        : '—'}
                    </div>
                  ) : null}
                </td>
                <td style={{ textAlign: 'right' }}>
                  {!p.isPrimary ? (
                    <button className="btn" onClick={() => setPrimary(p.id)} disabled={busyPerson.primaryId === p.id}>
                      {busyPerson.primaryId === p.id ? 'Saving…' : 'Set primary'}
                    </button>
                  ) : null}{' '}
                  <button
                    className="btn"
                    disabled={!p.email || busyPerson.emailId === p.id}
                    title={!p.email ? 'Person has no email' : ''}
                    onClick={() => sendEmail(p.id)}
                  >
                    {busyPerson.emailId === p.id ? 'Sending…' : 'Send email'}
                  </button>{' '}
                  <button className="btn" onClick={() => genLink(p.id)} disabled={busyPerson.linkId === p.id}>
                    {busyPerson.linkId === p.id ? 'Working…' : (linksByPerson.get(p.id) || []).length ? 'Copy link' : 'Generate link'}
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="card">
        <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap', alignItems: 'baseline' }}>
          <div style={{ fontSize: 16, fontWeight: 900 }}>Activity timeline</div>
          <div className="muted" style={{ fontSize: 12 }}>
            Notes, meetings, portal visits, and email events
          </div>
        </div>
        <div style={{ height: 10 }} />

        {(data.timeline || []).length ? (
          <div style={{ display: 'grid', gap: 10 }}>
            {(data.timeline || []).map((t) => (
              <div
                key={t.id}
                style={{
                  border: '1px solid var(--border)',
                  borderRadius: 14,
                  padding: 12,
                  display: 'grid',
                  gap: 6,
                }}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap', alignItems: 'baseline' }}>
                  <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                    <span className="pill">
                      {t.kind === 'visit' ? 'Visit' : t.kind === 'meeting' ? 'Meeting' : t.kind === 'email' ? 'Email' : 'Note'}
                    </span>
                    <span style={{ fontWeight: 850 }}>{t.title}</span>
                  </div>
                  <div className="muted" style={{ fontSize: 12, fontVariantNumeric: 'tabular-nums' }}>
                    {String(t.occurredAt).slice(0, 19).replace('T', ' ')}
                  </div>
                </div>
                <div style={{ whiteSpace: 'pre-wrap' }}>{t.content}</div>
              </div>
            ))}
          </div>
        ) : (
          <div className="muted">No activity yet.</div>
        )}

        <div className="hr" />
        <div style={{ fontSize: 16, fontWeight: 900, marginBottom: 10 }}>Add note / meeting</div>
        <div className="row">
          <div>
            <div className="muted" style={{ fontSize: 12, marginBottom: 6 }}>
              Type
            </div>
            <select className="select" value={activityType} onChange={(e) => setActivityType(e.target.value as any)}>
              <option value="note">Note</option>
              <option value="meeting">Meeting</option>
            </select>
          </div>
          <div>
            <div className="muted" style={{ fontSize: 12, marginBottom: 6 }}>
              Date
            </div>
            <input className="input" type="date" value={activityDate} onChange={(e) => setActivityDate(e.target.value)} />
          </div>
        </div>
        <div style={{ height: 10 }} />
        <div>
          <div className="muted" style={{ fontSize: 12, marginBottom: 6 }}>
            Content
          </div>
          <textarea className="textarea" value={activityContent} onChange={(e) => setActivityContent(e.target.value)} placeholder="Notes..." />
        </div>
        <div style={{ height: 10 }} />
        <button className="btn primary" disabled={!activityContent.trim() || addingActivity} onClick={addActivity}>
          {addingActivity ? 'Saving…' : 'Save'}
        </button>
      </div>
    </div>
  );
}

function FolderAllowRow({
  folder,
  children,
  allowedSet,
  onToggle,
}: {
  folder: FolderNode;
  children: Map<string | null, FolderNode[]>;
  allowedSet: Set<string>;
  onToggle: (id: string) => void;
}) {
  const kids = children.get(folder.id) || [];
  return (
    <li>
      <label style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
        <input type="checkbox" checked={allowedSet.has(folder.id)} onChange={() => onToggle(folder.id)} />
        <span>{folder.name}</span>
      </label>
      {kids.length ? (
        <ul>
          {kids.map((k) => (
            <FolderAllowRow key={k.id} folder={k} children={children} allowedSet={allowedSet} onToggle={onToggle} />
          ))}
        </ul>
      ) : null}
    </li>
  );
}
